import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    ImageOpNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {Vector2} from "@cm/math"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/hierarchical-cross-correlation/definition"
import {ImageOpNodeNormalizedCrossCorrelation} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/normalized-cross-correlation/node"
import {ImageOpNodeTilingFindCorrelationPeak} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-correlation-peak/node"
import {ImageOpNodeTilingPenalizeByDistance} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/penalize-by-distance/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {ImageOpNodeTilingGenerateWeightMask} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/generate-weight-mask/node"
import {isImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeTilingHierarchicalCrossCorrelation"}

@registerImageOpNode
export class ImageOpNodeTilingHierarchicalCrossCorrelation extends ImageOpNode<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    override dispose(): void {
        this.imageOpNodeBag.dispose()
        super.dispose()
    }

    override run = async (evaluator: Evaluator<AnyImageOpContext>): Promise<ImageOpOutputs<typeof definition>> => {
        const {
            imageOpHalOptions,
            sourceImage,
            sourceImageReferencePosition: sourceImageReferencePositionIn,
            templateImage,
            templateImageReferencePosition: templateImageReferencePositionIn,
            maxSearchRadius,
            correlationPenaltyPerPixel,
            penaltyDirectionX,
        } = await evaluator.getAll(this.parameters)

        this.imageOpNodeBag.beginFrame()

        const correlationNeighborhood = 8 // in each direction
        const searchOffset = -1
        const searchSize = 4

        const sourceImageReferencePosition = Vector2.fromVector2Like(sourceImageReferencePositionIn).floorInPlace()
        const templateImageReferencePosition = Vector2.fromVector2Like(templateImageReferencePositionIn).floorInPlace()
        const minSourceSize = Math.min(sourceImage.descriptor.width, sourceImage.descriptor.height)
        const minTemplateSize = Math.min(templateImage.descriptor.width, templateImage.descriptor.height)
        const minSize = Math.min(minSourceSize, minTemplateSize)
        const maxLevels = Math.ceil(Math.log2(minSize)) + 1
        const numLevels = Math.min(maxLevels, Math.ceil(Math.log2(maxSearchRadius)))

        let sourceOffset = Vector2.fromVector2Like(sourceImageReferencePosition)
            .divInPlace(2 ** (numLevels - 1))
            .floorInPlace()
        let correlation = 0
        const vecCorrelationPenaltyPerPixel =
            typeof correlationPenaltyPerPixel === "number"
                ? new Vector2(correlationPenaltyPerPixel, correlationPenaltyPerPixel)
                : Vector2.fromVector2Like(correlationPenaltyPerPixel)
        for (let level = numLevels - 1; level >= 0; level--) {
            const levelScale = 2 ** level
            // cross-correlation
            // const templateOffset = new Vector2(
            //     templateRegion.width / levelScale / 2 - correlationWindowSize / 2,
            //     templateRegion.height / levelScale / 2 - correlationWindowSize / 2,
            // )
            // if (showDebugInfo && debugRectFn) {
            //     using sourceOffsetAndCorrelationWebGl2 = await imageOpContextWebGL2.getImage(sourceOffsetAndCorrelation)
            //     const sourceOffsetAndCorrelationData = await sourceOffsetAndCorrelationWebGl2.ref.halImage.readRawImageData("float32")
            //     const sourceOffset = new Vector2(sourceOffsetAndCorrelationData[0], sourceOffsetAndCorrelationData[1])
            //     debugRectFn(
            //         {
            //             x: sourceRegion.x + (sourceOffset.x << level),
            //             y: sourceRegion.y + (sourceOffset.y << level),
            //             width: (correlationWindowSize + searchSize - 1) << level,
            //             height: (correlationWindowSize + searchSize - 1) << level,
            //         },
            //         "blue",
            //     )
            // }
            const targetOffset = Vector2.fromVector2Like(templateImageReferencePosition).divInPlace(levelScale).floorInPlace()

            const sourceRegion = {
                x: sourceOffset.x - correlationNeighborhood + searchOffset,
                y: sourceOffset.y - correlationNeighborhood + searchOffset,
                width: 1 + 2 * correlationNeighborhood + searchSize - 1,
                height: 1 + 2 * correlationNeighborhood + searchSize - 1,
            }
            const levelSourceImage = this.imageOpNodeBag.getNode(`ExtractLevelSource${level}`, ImageOpNodeExtractRegion, {
                imageOpHalOptions,
                sourceImage,
                mipLevelOffset: level,
                region: sourceRegion,
                addressMode: "border",
            })
            const levelSourceWeightImage = this.imageOpNodeBag.getNode(`GenerateLevelSourceWeights${level}`, ImageOpNodeTilingGenerateWeightMask, {
                imageOpHalOptions,
                sourceImage,
                mipLevelOffset: level,
                region: sourceRegion,
            })

            const templateRegion = {
                x: targetOffset.x - correlationNeighborhood,
                y: targetOffset.y - correlationNeighborhood,
                width: 1 + 2 * correlationNeighborhood,
                height: 1 + 2 * correlationNeighborhood,
            }
            const levelTemplateImage = this.imageOpNodeBag.getNode(`ExtractLevelTemplate${level}`, ImageOpNodeExtractRegion, {
                imageOpHalOptions,
                sourceImage: templateImage,
                mipLevelOffset: level,
                region: templateRegion,
                addressMode: "border",
            })
            const levelTemplateWeightImage = this.imageOpNodeBag.getNode(`GenerateLevelTemplateWeights${level}`, ImageOpNodeTilingGenerateWeightMask, {
                imageOpHalOptions,
                sourceImage: templateImage,
                mipLevelOffset: level,
                region: templateRegion,
            })

            const crossCorrelation: AnyImageOpResultNode = this.imageOpNodeBag.getNode(`NCC${level}`, ImageOpNodeNormalizedCrossCorrelation, {
                imageOpHalOptions,
                sourceImage: levelSourceImage,
                sourceWeightImage: levelSourceWeightImage,
                templateImage: levelTemplateImage,
                templateWeightImage: levelTemplateWeightImage,
                options: {premultipliedImages: false},
                // debugImage: debugImage,
            })

            const distancePenalizedCorrelation = this.imageOpNodeBag.getNode(`PenalizeByDistance${level}`, ImageOpNodeTilingPenalizeByDistance, {
                imageOpHalOptions,
                correlation: crossCorrelation,
                offset: {
                    x: sourceOffset.x + searchOffset,
                    y: sourceOffset.y + searchOffset,
                },
                referencePosition: sourceImageReferencePosition,
                imageSize: sourceImage.descriptor,
                level: level,
                penaltyPerPixel: vecCorrelationPenaltyPerPixel,
                penaltyDirectionX: penaltyDirectionX,
            })
            // debugImage?.addImage(distancePenalizedCorrelation, {scale: 0.5, offset: 0.5})
            const maxValueAndIndex = this.imageOpNodeBag.getNode(`FindCorrelationPeak${level}`, ImageOpNodeTilingFindCorrelationPeak, {
                imageOpHalOptions,
                correlation: distancePenalizedCorrelation,
            })

            const evaluatedMaxValueAndIndex = await evaluator.get(maxValueAndIndex)
            if (!isImageOpHal(evaluatedMaxValueAndIndex)) {
                throw new Error("Expected max value and index to be an ImageOpHal")
            }

            const maxValueAndIndexHalImage = await evaluatedMaxValueAndIndex.getUpdatedHalImage({
                x: 0,
                y: 0,
                width: evaluatedMaxValueAndIndex.descriptor.width,
                height: evaluatedMaxValueAndIndex.descriptor.height,
                mipLevel: 0,
            })
            const maxValueAndIndexData = maxValueAndIndexHalImage.halImage.readImageDataFloat()
            maxValueAndIndexHalImage.release()
            const peakOffset = new Vector2(maxValueAndIndexData[1], maxValueAndIndexData[2])
            const peakCorrelation = maxValueAndIndexData[0]

            // console.log(`Peak found at level ${level}: Index: ${peakOffset.x}, ${peakOffset.y}, Value: ${peakCorrelation}`)

            // if (showDebugInfo) {
            //     const sourceOffsetData = await sourceOffsetWebGl2.ref.halImage.readRawImageData("float32")
            //     const sourceOffset = new Vector2(sourceOffsetData[0], sourceOffsetData[1])
            //     const maxValueAndIndexData = await maxValueAndIndexWebGL2.ref.halImage.readRawImageData("float32")
            //     const maxIndex = new Vector2(maxValueAndIndexData[1], maxValueAndIndexData[2])
            //     const maxCorrelation = maxValueAndIndexData[0]
            //     console.log(`Peak found at level ${level}: Index: ${maxIndex.x}, ${maxIndex.y}, Value: ${maxCorrelation}`)
            //     // const secondMaxValueAndIndexData = await secondMaxValueAndIndexWebGL2.ref.halImage.readRawImageData("float32")
            //     // const secondMaxIndex = new Vector2(secondMaxValueAndIndexData[1], secondMaxValueAndIndexData[2])
            //     // const secondMaxCorrelation = secondMaxValueAndIndexData[0]
            //     // console.log(`Second peak found at level ${level}: Index: ${secondMaxIndex.x}, ${secondMaxIndex.y}, Value: ${secondMaxCorrelation}`)
            //     if (debugRectFn) {
            //         // draw peak box
            //         debugRectFn(
            //             {
            //                 x: sourceRegion.x + (sourceOffset.x << level),
            //                 y: sourceRegion.y + (sourceOffset.y << level),
            //                 width: correlationWindowSize << level,
            //                 height: correlationWindowSize << level,
            //             },
            //             "lightblue",
            //         )
            //         debugRectFn(
            //             {
            //                 x: templateRegion.x + (templateOffset.x << level),
            //                 y: templateRegion.y + (templateOffset.y << level),
            //                 width: correlationWindowSize << level,
            //                 height: correlationWindowSize << level,
            //             },
            //             "lightgreen",
            //         )
            //     }
            // }

            sourceOffset.addInPlace(peakOffset).addInPlace(searchOffset)
            correlation += peakCorrelation

            if (level === 0) {
                break
            }

            sourceOffset = sourceOffset.mul(2)
        }

        this.imageOpNodeBag.endFrame()

        correlation /= numLevels // divide newCorrelation by numLevels to get the final averaged correlation across levels
        return {
            sourceOffset,
            correlation,
        }
    }

    private imageOpNodeBag = new ImageOpNodeBag("TilingHierarchicalCrossCorrelation")
}
